import { makeObservable, observable, action, runInAction, reaction } from 'mobx';
import {
  searchSystemPrompts,
  addSystemPrompt,
  updateSystemPrompt,
  removeSystemPrompt,
} from '@/api/systemPrompts';
import type { SystemPromptDto } from '@/api/types';
import type { RootStore } from './RootStore';

// ai.SystemPrompt — instruction blocks linked into agents via the capability
// composition pipeline. Merchant-scoped (filtered by auth context server-side).

export class SystemPromptStore {
  @observable items: SystemPromptDto[] = [];
  @observable totalCount = 0;
  @observable isLoading = false;
  @observable error: string | null = null;

  @observable search = '';
  @observable globalsOnly = false;

  constructor(private root: RootStore) {
    makeObservable(this);

    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        if (merchantId != null) {
          void this.fetch();
        } else {
          runInAction(() => {
            this.items = [];
            this.totalCount = 0;
          });
        }
      },
      { fireImmediately: true }
    );
  }

  @action setSearch(v: string): void { this.search = v; }
  @action setGlobalsOnly(v: boolean): void { this.globalsOnly = v; }

  @action
  async fetch(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const result = await searchSystemPrompts({
        search: this.search,
        globals: this.globalsOnly || undefined,
        pageSize: 200,
      });
      runInAction(() => {
        this.items = result.data ?? [];
        this.totalCount = result.paging?.totalRecordCount ?? this.items.length;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load system prompts';
      });
    }
  }

  @action
  async create(input: Partial<SystemPromptDto>): Promise<SystemPromptDto> {
    const created = await addSystemPrompt(input);
    void this.fetch();
    return created;
  }

  @action
  async update(input: Partial<SystemPromptDto>): Promise<SystemPromptDto> {
    const updated = await updateSystemPrompt(input);
    void this.fetch();
    return updated;
  }

  @action
  async remove(id: number): Promise<void> {
    await removeSystemPrompt(id);
    runInAction(() => {
      this.items = this.items.filter((p) => p.id !== id);
      this.totalCount = Math.max(0, this.totalCount - 1);
    });
  }
}
